/**
 * 登录表单组件
 */

import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { LoginCredentials } from '../../types/auth';

interface LoginFormProps {
  onSwitchToRegister: () => void;
  onForgotPassword?: () => void;
  onNeedVerification?: (email: string) => void;
}

export function LoginForm({ onSwitchToRegister, onForgotPassword, onNeedVerification }: LoginFormProps): JSX.Element {
  const { login, clearError } = useAuth();
  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: '',
  });
  const [loginError, setLoginError] = useState<string>('');
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // 清除错误信息
    if (loginError) {
      setLoginError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.email && formData.password) {
      setIsLoggingIn(true);
      setLoginError('');
      clearError();
      
      try {
        const result = await login(formData);
        
        // 检查是否需要邮箱验证
        if (result.requiresVerification && onNeedVerification) {
          onNeedVerification(formData.email);
        }
        // 如果不需要验证，AuthContext会自动处理登录状态
      } catch (err) {
        // 本地处理登录错误
        const errorMessage = err instanceof Error ? err.message : '登录失败，请重试';
        setLoginError(errorMessage);
        console.error('登录失败:', err);
      } finally {
        setIsLoggingIn(false);
      }
    }
  };

  return (
    <div className="w-full max-w-md">
      <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">
        登录 PromptMaster
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* 邮箱输入 */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            邮箱地址
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            disabled={isLoggingIn}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            placeholder="请输入您的邮箱"
          />
        </div>

        {/* 密码输入 */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            密码
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            required
            disabled={isLoggingIn}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            placeholder="请输入您的密码"
          />
        </div>

        {/* 错误信息 */}
        {loginError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
            {loginError}
          </div>
        )}

        {/* 登录按钮 */}
        <button
          type="submit"
          disabled={isLoggingIn || !formData.email || !formData.password}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
        >
          {isLoggingIn ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              登录中...
            </span>
          ) : (
            '登录'
          )}
        </button>
      </form>

      {/* 忘记密码链接 */}
      {onForgotPassword && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={onForgotPassword}
            disabled={isLoggingIn}
            className="text-sm text-blue-600 hover:text-blue-500 disabled:text-blue-300"
          >
            忘记密码？
          </button>
        </div>
      )}

      {/* 切换到注册 */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          还没有账户？{' '}
          <button
            type="button"
            onClick={onSwitchToRegister}
            disabled={isLoggingIn}
            className="text-blue-600 hover:text-blue-500 font-medium disabled:text-blue-300"
          >
            立即注册
          </button>
        </p>
      </div>
    </div>
  );
} 